import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  countriesFilterSelector,
  typesFilterSelector,
  stagesFilterSelector,
  scoresFilterSelector,
  managerTextFilterSelector,
  managerFilterSelector,
  wholeFilterSelector,
  termFilterSelector,
  bookingActivityFilterSelector,
} from 'store/modules/crm/selectors';
import { IMultiselectValueLabelPair, Multiselect } from 'ui/Multiselect';
import SingleSelect, { ISingleSelectOption } from 'ui/SingleSelect';
import TextInput from 'pureUi/TextInput';
import MagnifyingIcon from 'ui/Icons/magnifying-glass.component.svg';
import { useCrmTypeValues } from 'services/CrmApi/queries/useCrmTypeValues';
import { EBookingActivityStatus } from 'services/CrmApi/types/DirectoryEntry';
import FluidButton from 'ui/FluidButton';
import CalendarCheckIcon from 'ui/Icons/tabler/calendar-check.component.svg';
import CalendarTimeIcon from 'ui/Icons/tabler/calendar-time.component.svg';
import HourglassEmptyIcon from 'ui/Icons/tabler/hourglass-empty.component.svg';
import CalendarXIcon from 'ui/Icons/tabler/calendar-x.component.svg';
import InfoIcon from 'ui/Icons/Info.component.svg';
import { Tooltip } from 'containers/TravelAgents/List/Tooltip';
import { CompanyIcon } from 'ui/Icons/components/Company.component';
import { theme } from '../../../../tailwind.config';
import * as AuthSelectors from 'store/modules/auth';
import { useResponsive } from '../hooks/useResponsive';
import { ContactIcon } from 'ui/Icons/components/CrmContact.component';
import { useHistory } from 'react-router';
import { ETypeValueType } from 'services/CrmApi/types/types';
import { useCrmCountries } from 'services/CrmApi/queries/useCrmCountries';
import * as CrmManagementActions from 'store/modules/crm/actions';
import { AsyncSearchDropdown } from 'ui/AsyncSearchDropdown';
import { userToOption } from 'ui/SingleSelect/helpers';
import { useBackendUsers } from 'services/BackendApi/queries/useBackendUsers';
import { ProgressBar } from 'ui/ProgressBar';
import classnames from 'classnames';
import { Rating } from 'react-simple-star-rating';
import { VerticalLine } from 'ui/VerticalLine';
import { CrmDirectorySubscriptionButtons } from './CrmDirectorySubscriptionButtons';

export interface ICrmFiltersProps {
  onSubscribeToAllNotifications: () => void;
  onUnsubscribeFromAllNotifications: () => void;
  handlePageChange: (page: number) => void;
  directoryEntriesCount: number;
}

export const CrmFilters: React.FC<ICrmFiltersProps> = React.memo(
  ({ onSubscribeToAllNotifications, onUnsubscribeFromAllNotifications, handlePageChange, directoryEntriesCount }) => {
    // Type codes are shared among companies/contacts (OWA-8657)
    const composeTypeValue = (value: string, profileType: ETypeValueType): string => `${value}-${profileType}`;
    const parseTypeValue = (composedValue: string): [string, ETypeValueType] =>
      composedValue.split('-') as [string, ETypeValueType];

    const dispatch = useDispatch();
    const { isMobile, isDesktop, isDesktopHD } = useResponsive();
    const history = useHistory();
    const isSr = useSelector(AuthSelectors.isSr);
    const isSales = useSelector(AuthSelectors.isSales);
    const isAdmin = useSelector(AuthSelectors.isAdmin);

    const managerFilter = useSelector(managerFilterSelector);
    const managerQueryText = useSelector(managerTextFilterSelector);
    const typesFilter = useSelector(typesFilterSelector).map(e => composeTypeValue(e.value, e.profileType));
    const countriesFilter = useSelector(countriesFilterSelector);
    const stageFilter = useSelector(stagesFilterSelector);
    const scoreFilter = useSelector(scoresFilterSelector)?.toString();
    const profileSearchValue = useSelector(termFilterSelector);
    const bookingActivityFilter = useSelector(bookingActivityFilterSelector);

    const managers = useBackendUsers(managerQueryText);
    const managerOptions: ISingleSelectOption[] = managers.data?.data?.data.map(user => userToOption(user)) ?? [];

    const crmTypeValuesCompanies = useCrmTypeValues(ETypeValueType.COMPANY, { enabled: true });
    const companyTypeOptions: IMultiselectValueLabelPair[] =
      crmTypeValuesCompanies.data?.data?.map(item => ({
        value: composeTypeValue(item.code, ETypeValueType.COMPANY),
        label: item.value,
        iconHtml: <CompanyIcon fill={theme.colors['gray-60']} />,
        iconAlign: 'end' as 'left' | 'right' | 'end',
      })) ?? [];

    const crmTypeValuesContacts = useCrmTypeValues(ETypeValueType.CONTACT, { enabled: true });
    const contactTypeOptions: IMultiselectValueLabelPair[] =
      crmTypeValuesContacts.data?.data?.map(item => ({
        value: composeTypeValue(item.code, ETypeValueType.CONTACT),
        label: item.value,
        iconHtml: <ContactIcon fill={theme.colors['gray-60']} />,
        iconAlign: 'end' as 'left' | 'right' | 'end',
      })) ?? [];

    const typeOptions = [...companyTypeOptions, ...contactTypeOptions].sort((a, b) => a.label.localeCompare(b.label));

    const crmCountries = useCrmCountries();
    const countryOptions: IMultiselectValueLabelPair[] =
      crmCountries.data?.data?.map(item => ({ value: item.code, label: item.name })) ?? [];

    const crmTypeValuesStages = useCrmTypeValues(ETypeValueType.LEAD_STAGE, { enabled: true });
    const crmTypeValuesStagesSorted = crmTypeValuesStages.data?.data?.sort((a, b) => a.order - b.order) || [];
    const stagesTypeOptions: ISingleSelectOption[] =
      crmTypeValuesStagesSorted.map(item => ({
        value: item.code,
        renderItem: className => (
          <div
            className={classnames(
              className,
              'flex items-center w-full justify-between font-pt-sans py-2 text-15px leading-19px'
            )}
          >
            <div>{item.value}</div>
            <div className="w-[50px]">
              <ProgressBar score={item.order} total={4} />
            </div>
          </div>
        ),
      })) ?? [];

    const starTypeOptions: ISingleSelectOption[] = [1, 2, 3, 4, 5].map(stars => ({
      value: stars.toString(),
      renderItem: className => (
        <div
          className={classnames(
            className,
            'flex items-center w-full justify-between font-pt-sans py-2 text-15px leading-19px'
          )}
        >
          <Rating size={14} initialValue={stars} readonly />
        </div>
      ),
    }));

    const scoresTypeOptions: ISingleSelectOption[] = [{ value: '0', label: 'Not Set' }, ...starTypeOptions];

    const bookingActivityOptions: IMultiselectValueLabelPair[] = [
      {
        value: EBookingActivityStatus.ACTIVE,
        label: 'Last booking < 12 months',
        iconHtml: <CalendarCheckIcon className="w-[18px] h-[18px] text-status-confirmed" />,
        iconAlign: 'end' as const,
      },
      {
        value: EBookingActivityStatus.INACTIVE,
        label: 'Last booking > 12 months',
        iconHtml: <CalendarTimeIcon className="w-[18px] h-[18px] text-status-requested" />,
        iconAlign: 'end' as const,
      },
      {
        value: EBookingActivityStatus.NOT_CONFIRMED,
        label: 'Enquiries No Bookings',
        iconHtml: <HourglassEmptyIcon className="w-[18px] h-[18px] text-status-enquiry" />,
        iconAlign: 'end' as const,
      },
      {
        value: EBookingActivityStatus.NO_BOOKINGS,
        label: 'No Enquiries No Bookings',
        iconHtml: <CalendarXIcon className="w-[18px] h-[18px] text-status-discarded" />,
        iconAlign: 'end' as const,
      },
    ];

    const canManageSubscription = isSales || isAdmin;

    const handleTypeFilterChange = useCallback(
      (selectedValues: string[]) => {
        const selectedValuesTyped = selectedValues.map(v => {
          const [code, profileType] = parseTypeValue(v);
          return {
            value: code,
            profileType,
          };
        });
        dispatch(CrmManagementActions.setTypeFilterAction(selectedValuesTyped));
        handlePageChange(1);
      },
      [dispatch, handlePageChange]
    );

    const handleCountryFilterChange = useCallback(
      (selectedValues: string[]) => {
        dispatch(CrmManagementActions.setCountryFilterAction(selectedValues));
        handlePageChange(1);
      },
      [dispatch, handlePageChange]
    );

    const handleStageFilterChange = useCallback(
      (selectedValue?: string) => {
        dispatch(CrmManagementActions.setStageFilterAction(selectedValue || null));
        handlePageChange(1);
      },
      [dispatch, handlePageChange]
    );

    const handleScoreFilterChange = useCallback(
      (selectedValue?: string) => {
        dispatch(CrmManagementActions.setScoreFilterAction(selectedValue ? parseInt(selectedValue) : null));
        handlePageChange(1);
      },
      [dispatch, handlePageChange]
    );

    const handleProfileSearchChange = useCallback(
      e => {
        dispatch(CrmManagementActions.setTermFilterAction(e.currentTarget.value));
        handlePageChange(1);
      },
      [dispatch, handlePageChange]
    );

    const handleManagerFilterChoose = useCallback(
      (value: string) => {
        const selectedManager = managerOptions.find(item => item.value === value);
        dispatch(CrmManagementActions.setManagerTextFilterAction(selectedManager?.label ?? ''));
        dispatch(CrmManagementActions.setManagerFilterAction(value));
        handlePageChange(1);
      },
      [managerOptions, dispatch, handlePageChange]
    );

    const handleManagerFilterChange = useCallback(
      async value => {
        if (value === '') {
          dispatch(CrmManagementActions.setManagerFilterAction(null));
        }
        dispatch(CrmManagementActions.setManagerTextFilterAction(value || ''));
        handlePageChange(1);
      },
      [dispatch, handlePageChange]
    );

    const handleBookingActivityFilterChange = useCallback(
      (selectedValues: string[]) => {
        dispatch(
          CrmManagementActions.setBookingActivityFilterAction(
            selectedValues.length > 0 ? (selectedValues[0] as EBookingActivityStatus) : null
          )
        );
        handlePageChange(1);
      },
      [dispatch, handlePageChange]
    );

    const handleResetFilters = useCallback(() => {
      dispatch(CrmManagementActions.resetWholeFilterAction());
      handlePageChange(1);
    }, [dispatch, handlePageChange]);

    const handleCreateCompany = useCallback(() => {
      history.push('/crm/company/create');
    }, [history]);

    const handleCreateContact = useCallback(() => {
      history.push('/crm/contact/create');
    }, [history]);

    return (
      <div className="task-list-filters mt-5 mb-5">
        <p className="task-list-filters-header font-pt-sans font-bold text-17px leading-22px">Filter Results</p>
        <div className="filters-container flex flex-wrap gap-x-[20px] gap-y-[15px] mt-10px">
          <div className="task-category-filter flex-1 min-w-[270px] max-w-[270px] relative top-[-2px]">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Profile</label>
            <TextInput
              id="crm-filter-profile"
              onChange={handleProfileSearchChange}
              placeholder="Search from directory ..."
              className="task-priority-filter min-w-[270px] max-w-[270px] my-5px"
              value={profileSearchValue}
              inputClassName="!py-[10px] !px-[30px] !text-black !bg-white-hard"
            >
              <MagnifyingIcon className="absolute w-[18px] top-[10px] left-[10px] fill-light-gray rotate-90" />
            </TextInput>
          </div>

          <div className="task-category-filter flex-1 relative top-[-2px] min-w-0">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Type</label>
            <Multiselect
              className="multiselect-category bg-ivory mt-[4px] text-15px text-black"
              selectedItemContentClassName="font-bold"
              itemCtaClassName="hover:bg-teal-20 text-left"
              itemsClassname="bg-ivory"
              onUpdate={handleTypeFilterChange}
              options={typeOptions}
              selectedValues={typesFilter}
              isIncludeClearButton
            />
          </div>

          {!isSales && (
            <AsyncSearchDropdown
              fieldId="manager"
              label="Manager"
              className="manager min-w-[180px] max-w-[180px] flex-1 text-black"
              value={managerQueryText ?? ''}
              selectedValue={managerFilter ?? undefined}
              options={managerOptions}
              onChoose={handleManagerFilterChoose}
              onChange={handleManagerFilterChange}
              maxVisibleItems={8}
              loading={managers.isLoading}
              errorMessage={null}
              errorClassName="assignee-error"
            />
          )}

          <div className="task-category-filter flex-1 min-w-[250px] max-w-[250px] relative top-[-2px]">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Country</label>
            <Multiselect
              selectedItemContentClassName="font-bold"
              className="multiselect-status bg-ivory mt-[4px] text-15px text-black"
              itemCtaClassName="hover:bg-teal-20 text-left"
              itemsClassname="bg-ivory"
              onUpdate={handleCountryFilterChange}
              options={countryOptions}
              selectedValues={countriesFilter}
              isIncludeClearButton
            />
          </div>

          <SingleSelect
            fieldId="task-priority-filter"
            label="Stage"
            className="task-priority-filter min-w-[180px] max-w-[180px] text-black"
            labelWhenNothingSelected="All"
            useCustomLabelWhenNothingSelected={true}
            value={stageFilter ?? undefined}
            options={stagesTypeOptions}
            onChange={handleStageFilterChange}
            maxVisibleItems={6}
          />

          <SingleSelect
            fieldId="task-priority-filter"
            label="Score above"
            className="task-priority-filter min-w-[180px] max-w-[180px] text-black"
            labelWhenNothingSelected="All"
            useCustomLabelWhenNothingSelected
            value={scoreFilter ?? undefined}
            options={scoresTypeOptions}
            onChange={handleScoreFilterChange}
            maxVisibleItems={6}
          />

          <div className="task-category-filter flex-1 min-w-[220px] max-w-[220px] relative top-[-2px]">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs mb-[6px] flex items-center gap-[4px]">
              Booking Activity
              <Tooltip content="Based on the activity of last 12 months.">
                <InfoIcon className="w-[14px] h-[14px] fill-gray-40 cursor-pointer" />
              </Tooltip>
            </label>
            <Multiselect
              className="multiselect-category bg-ivory text-15px text-black"
              selectedItemContentClassName="font-bold"
              itemCtaClassName="hover:bg-teal-20 text-left"
              itemsClassname="bg-ivory"
              onUpdate={handleBookingActivityFilterChange}
              options={bookingActivityOptions}
              selectedValues={bookingActivityFilter ? [bookingActivityFilter] : []}
              isSingleSelectMode
              hideCheckboxes
              isCloseOnSelect
              isIncludeClearButton
              placeholderText="Any"
            />
          </div>
        </div>
        <div className="button-container flex flex-col mt-[15px] crm-tablet:flex-col-reverse crm-desktop:flex-row crm-desktop:gap-[10px]">
          <div className="first-button-container flex gap-[10px] mt-[15px] crm-desktop:mt-0">
            {canManageSubscription && (
              <CrmDirectorySubscriptionButtons
                directoryEntriesCount={directoryEntriesCount}
                onSubscribeToAllNotifications={onSubscribeToAllNotifications}
                onUnsubscribeFromAllNotifications={onUnsubscribeFromAllNotifications}
              />
            )}
          </div>
          {(isDesktop || isDesktopHD) && (
            <VerticalLine
              height="auto"
              color={theme.colors['gray-10']}
              borderWidth={2}
              borderRadius={4}
              className="flex"
            />
          )}
          <div className="second-button-container flex justify-between mt-[15px] flex-1 crm-desktop:mt-0">
            <div className="buttons-container-left flex items-center gap-[10px]">
              <FluidButton type="secondary" textClassName="flex items-center gap-[10px]" onClick={handleResetFilters}>
                Reset Filters
              </FluidButton>
            </div>
            <div className="buttons-container-right flex items-center gap-[10px]">
              <FluidButton
                type="primary"
                textClassName="flex items-center gap-[10px]"
                onClick={handleCreateCompany}
                disabled={isSr}
              >
                New Company
                {isMobile ? null : <CompanyIcon fill={theme.colors['white']} />}
              </FluidButton>
              <FluidButton
                type="primary"
                textClassName="flex items-center gap-[10px]"
                onClick={handleCreateContact}
                disabled={isSr}
              >
                New Contact
                {isMobile ? null : <ContactIcon fill={theme.colors['white']} />}
              </FluidButton>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
